import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { removeAudioNoise, stepOutputFormat } from "./operations";
import { measureNoiseFloorDb, noiseFilterNf, measureNoiseReductionDb } from "./noise";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, useAudioPipelineStep } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Reduces steady background noise — hum, hiss, fan noise, air conditioning — from an audio recording, using spectral subtraction at three strengths, plus a fourth Voice mode that applies a small noise-suppression model loaded from this site. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file, choose how aggressive the cleanup should be, and download the result.`,
  tip: `Being upfront about what this can and can't do: this handles steady, constant background noise well — the kind of hum or hiss that's always there at roughly the same level. It's not the same as the AI-based voice isolation in tools like Krisp or Adobe Podcast Enhance, which can separate a voice from much more chaotic or changing background noise (like a crowded room). For a steady noise floor, this genuinely helps; for a complex or shifting noisy environment, results will be more limited.`,
};

// nr = dB of noise to remove; offsetDb = how far above the measured noise floor the filter treats sound as noise.
const STRENGTH_OPTIONS = [
  { label: "Light", value: 10, nr: 12, offsetDb: 8 },
  { label: "Standard", value: 20, nr: 20, offsetDb: 13 },
  { label: "Aggressive", value: 35, nr: 35, offsetDb: 20 },
  { label: "Voice (AI)", value: 99, nr: 0, offsetDb: 0 },
];

async function decode(src: Blob): Promise<{ chans: Float32Array[]; sampleRate: number }> {
  const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
  const ctx = new AudioCtx();
  try {
    const buf = await ctx.decodeAudioData(await src.arrayBuffer());
    return { chans: Array.from({ length: Math.min(2, buf.numberOfChannels) }, (_, c) => buf.getChannelData(c).slice()), sampleRate: buf.sampleRate };
  } finally { ctx.close().catch(() => {}); }
}

export default function NoiseRemover() {
  const { inPipeline, intermediate } = useAudioPipelineStep();
  const [file, setFile] = useState<File | null>(null);
  const [strength, setStrength] = useState(20);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [summary, setSummary] = useState("");
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const removeNoise = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      // Measure this file's own noise floor, then let afftdn (spectral subtraction) remove it. Decoding can fail
      // for odd formats; in that case fall back to a typical floor.
      setStatusMessage("Measuring the noise...");
      const preset = STRENGTH_OPTIONS.find((o) => o.value === strength) ?? STRENGTH_OPTIONS[1];
      let floorBefore = -50;
      let original: { chans: Float32Array[]; sampleRate: number } | null = null;
      try { original = await decode(file); floorBefore = measureNoiseFloorDb(original.chans, original.sampleRate); } catch { /* keep default */ }
      const nf = noiseFilterNf(floorBefore, preset.offsetDb);

      setStatusMessage("Removing background noise...");
      const fmtOut = stepOutputFormat(file, intermediate);
      let rnn: Uint8Array | undefined;
      if (preset.value === 99) {
        setStatusMessage("Loading the voice model...");
        rnn = new Uint8Array(await (await fetch("/models/rnnoise-voice.rnnn")).arrayBuffer());
      }
      const data = await removeAudioNoise(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, preset.nr, nf, setProgress, controller.signal, fmtOut.codec, rnn);
      try {
        if (!original) throw new Error("no decode");
        const after = await decode(new Blob([data as BlobPart], { type: fmtOut.mime }));
        // Same quiet stretches before and after, so the figure matches what you would measure in the pauses.
        const drop = Math.round(measureNoiseReductionDb(original.chans, after.chans, original.sampleRate));
        setSummary(drop >= 1 ? `Noise in the quiet parts reduced by about ${drop} dB (measured in the quietest 10% of the recording).` : "Little steady background noise was found to remove.");
      } catch { setSummary(""); }

      setResult(ffmpegOutputToBlob(data, fmtOut.mime));
      toast.success("Noise reduced!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file" description="Reduce steady background noise" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <div className="flex flex-wrap gap-2">
            {STRENGTH_OPTIONS.map(opt => (
              <Button key={opt.label} size="sm" variant={strength === opt.value ? "default" : "outline"} onClick={() => { setStrength(opt.value); setResult(null); }}>
                {opt.label}
              </Button>
            ))}
          </div>

          {strength === 99 && <p className="text-xs text-muted-foreground">Voice (AI) uses a small neural network (RNNoise) trained on speech. It handles changing noise such as fans, traffic and keyboards much better than the other levels, but is meant for spoken voice, not music.</p>}
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && summary && <p className="text-sm text-muted-foreground" data-testid="noise-summary">{summary}</p>}
          {result && <ResultPreview blob={result} />}
          <div className="flex flex-wrap gap-3 min-w-0">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`denoised_${file.name.replace(/\.[^.]+$/, `.${stepOutputFormat(file, intermediate).value}`)}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={removeNoise} disabled={processing || sizeCheck?.severity !== "ok"}>{processing ? "Processing..." : "Remove Noise"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
